import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Mail, Phone, BookOpen, MessageCircle, Search, LayoutGrid, List, Loader, Network, Calendar } from 'lucide-react';
import { academyAPI } from '../../services/api';
import OrgChartView from './OrgChartView';
import { resolveMediaUrl } from '../../utils/helpers';

export const ROLE_COLORS = {
  owner:           'bg-purple-100 text-purple-700',
  admin:           'bg-red-100 text-red-700',
  coach:           'bg-yellow-100 text-yellow-700',
  trainer:         'bg-blue-100 text-blue-700',
  physiotherapist: 'bg-teal-100 text-teal-700',
  support:         'bg-gray-100 text-gray-700',
};

export const NA = <span className="text-gray-400 italic">N/A</span>;

export function Avatar({ member }) {
  const initials = member.name
    ? member.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';
  if (member.profileImage) {
    return (
      <img
        src={resolveMediaUrl(member.profileImage)}
        alt={member.name}
        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
        onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
      {initials}
    </div>
  );
}

export function AvatarLg({ member }) {
  const initials = member.name
    ? member.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';
  if (member.profileImage) {
    return (
      <img
        src={resolveMediaUrl(member.profileImage)}
        alt={member.name}
        className="w-14 h-14 rounded-full object-cover flex-shrink-0"
        onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="w-14 h-14 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
      {initials}
    </div>
  );
}

export function ReachOutButtons({ member, onMessage }) {
  return (
    <div className="flex gap-2">
      {member.email ? (
        <a
          href={`mailto:${member.email}`}
          className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
          title={`Email ${member.name}`}
        >
          <Mail size={15} />
        </a>
      ) : (
        <span className="p-2 rounded-lg bg-gray-50 text-gray-300 cursor-not-allowed" title="No email">
          <Mail size={15} />
        </span>
      )}
      {member.phoneNumber ? (
        <a
          href={`tel:${member.phoneNumber}`}
          className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
          title={`Call ${member.name}`}
        >
          <Phone size={15} />
        </a>
      ) : (
        <span className="p-2 rounded-lg bg-gray-50 text-gray-300 cursor-not-allowed" title="Phone not available">
          <Phone size={15} />
        </span>
      )}
      <button
        onClick={() => onMessage(member)}
        disabled={!onMessage}
        className={`p-2 rounded-lg transition-colors ${
          onMessage
            ? 'bg-purple-50 text-purple-600 hover:bg-purple-100'
            : 'bg-gray-50 text-gray-300 cursor-not-allowed'
        }`}
        title={onMessage ? `Message ${member.name}` : 'Messaging unavailable here'}
      >
        <MessageCircle size={15} />
      </button>
    </div>
  );
}

// Stub only — scheduling ships in a separate ticket. Always disabled.
export function ViewScheduleButton({ compact = false }) {
  return (
    <button
      type="button"
      disabled
      title="Scheduling is coming soon in a separate release"
      className={`inline-flex items-center gap-1.5 rounded-lg border border-gray-200 text-gray-400 cursor-not-allowed ${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}
    >
      <Calendar size={compact ? 13 : 14} />
      View Schedule
    </button>
  );
}

function TableView({ staff, theme, onMessage }) {
  return (
    <div className={`${theme.card} rounded-xl border ${theme.border} overflow-hidden`}>
      <table className="w-full text-sm">
        <thead>
          <tr className={`border-b ${theme.border} ${theme.background}`}>
            <th className={`text-left px-4 py-3 font-semibold ${theme.textSecondary}`}>Staff Member</th>
            <th className={`text-left px-4 py-3 font-semibold ${theme.textSecondary} hidden md:table-cell`}>Contact</th>
            <th className={`text-left px-4 py-3 font-semibold ${theme.textSecondary} hidden lg:table-cell`}>Specializations</th>
            <th className={`text-left px-4 py-3 font-semibold ${theme.textSecondary} hidden lg:table-cell`}>Classes</th>
            <th className={`text-left px-4 py-3 font-semibold ${theme.textSecondary}`}>Reach Out</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {staff.map(member => {
            const roleColor = ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-700';
            return (
              <tr key={member.id} className={`${theme.cardHover} transition-colors`}>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <Avatar member={member} />
                    <div>
                      <p className={`font-semibold ${theme.text}`}>{member.name}</p>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${roleColor}`}>
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </span>
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4 hidden md:table-cell">
                  <div className="space-y-1">
                    <p className={`text-xs ${theme.textSecondary} flex items-center gap-1.5`}>
                      <Mail size={12} className={`flex-shrink-0 ${member.email ? '' : 'text-gray-300'}`} />
                      {member.email || NA}
                    </p>
                    <p className={`text-xs ${theme.textSecondary} flex items-center gap-1.5`}>
                      <Phone size={12} className={`flex-shrink-0 ${member.phoneNumber ? '' : 'text-gray-300'}`} />
                      {member.phoneNumber || NA}
                    </p>
                  </div>
                </td>

                <td className="px-4 py-4 hidden lg:table-cell">
                  <span className={`text-xs`}>{NA}</span>
                </td>

                <td className="px-4 py-4 hidden lg:table-cell">
                  <span className={`text-xs`}>{NA}</span>
                </td>

                <td className="px-4 py-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ReachOutButtons member={member} onMessage={onMessage} />
                    <ViewScheduleButton compact />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CardView({ staff, theme, onMessage }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {staff.map(member => {
        const roleColor = ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-700';
        return (
          <div key={member.id} className={`${theme.card} border ${theme.border} rounded-xl p-5 flex flex-col gap-4`}>
            <div className="flex items-start gap-3">
              <AvatarLg member={member} />
              <div className="flex-1 min-w-0">
                <p className={`font-semibold ${theme.text} truncate`}>{member.name}</p>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${roleColor}`}>
                  {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                </span>
              </div>
            </div>

            <div className="space-y-1">
              <p className={`text-xs ${theme.textSecondary} flex items-center gap-1.5`}>
                <Mail size={12} className="flex-shrink-0" />
                {member.email ? <span className="truncate">{member.email}</span> : NA}
              </p>
              <p className={`text-xs ${theme.textSecondary} flex items-center gap-1.5`}>
                <Phone size={12} className="flex-shrink-0" />
                {member.phoneNumber ? member.phoneNumber : NA}
              </p>
            </div>

            <div>
              <p className={`text-xs font-semibold ${theme.textSecondary} mb-1 uppercase tracking-wide`}>Specializations</p>
              <p className={`text-xs ${theme.textSecondary}`}>{NA}</p>
            </div>

            <div>
              <p className={`text-xs font-semibold ${theme.textSecondary} mb-1 uppercase tracking-wide`}>Classes</p>
              <p className={`text-xs ${theme.textSecondary} flex items-center gap-1.5`}>
                <BookOpen size={11} className="flex-shrink-0 text-blue-400" />{NA}
              </p>
            </div>

            <div className="mt-auto pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <span className={`text-xs ${theme.textMuted}`}>Reach out</span>
              <ReachOutButtons member={member} onMessage={onMessage} />
            </div>
            <ViewScheduleButton />
          </div>
        );
      })}
    </div>
  );
}

export default function StaffDirectoryTab({ academyId, onOpenMessages }) {
  const { theme } = useTheme();
  const [staff, setStaff]             = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [layout, setLayout]           = useState('list'); // 'list' | 'orgchart'
  const [viewMode, setViewMode]       = useState('table'); // 'table' | 'cards' (list layout only)

  useEffect(() => {
    if (!academyId) return;
    setLoading(true);
    Promise.all([
      academyAPI.staff.list(academyId).catch(() => []),
      academyAPI.openPositions.list(academyId).catch(() => []),
    ])
      .then(([staffData, positionsData]) => {
        setStaff(Array.isArray(staffData) ? staffData : []);
        setOpenPositions(Array.isArray(positionsData) ? positionsData : []);
      })
      .finally(() => setLoading(false));
  }, [academyId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(m =>
      m.name?.toLowerCase().includes(q) ||
      m.role?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q)
    );
  }, [staff, search]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className={`text-xl font-bold ${theme.text}`}>Staff Directory</h2>
          <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
            {loading ? 'Loading…' : `${staff.length} staff member${staff.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className={`flex items-center gap-1 p-1 border ${theme.border} rounded-lg ${theme.card}`}>
            <button
              onClick={() => setLayout('list')}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${layout === 'list' ? theme.tabActive : theme.textMuted}`}
              title="List view"
            >
              List
            </button>
            <button
              onClick={() => setLayout('orgchart')}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${layout === 'orgchart' ? theme.tabActive : theme.textMuted}`}
              title="Organizational chart view"
            >
              <Network size={13} /> Organizational
            </button>
          </div>

          {layout === 'list' && (
            <>
              <div className={`flex items-center gap-2 px-3 py-2 border ${theme.border} rounded-lg ${theme.card} flex-1 sm:w-56`}>
                <Search size={15} className={theme.textMuted} />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search staff…"
                  className={`bg-transparent text-sm ${theme.text} outline-none w-full`}
                />
              </div>

              <div className={`flex items-center gap-1 p-1 border ${theme.border} rounded-lg ${theme.card}`}>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? theme.tabActive : theme.textMuted}`}
                  title="Table view"
                >
                  <List size={16} />
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? theme.tabActive : theme.textMuted}`}
                  title="Card view"
                >
                  <LayoutGrid size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : layout === 'orgchart' ? (
        staff.length === 0 && openPositions.filter(p => p.status === 'open').length === 0 ? (
          <div className="text-center py-16">
            <p className={`text-lg font-semibold ${theme.text}`}>No staff yet</p>
            <p className={`text-sm ${theme.textMuted} mt-1`}>The org chart will appear here once staff are added.</p>
          </div>
        ) : (
          <OrgChartView staff={staff} openPositions={openPositions} theme={theme} onMessage={onOpenMessages} />
        )
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className={`text-lg font-semibold ${theme.text}`}>
            {search ? 'No staff match your search' : 'No staff yet'}
          </p>
          <p className={`text-sm ${theme.textMuted} mt-1`}>
            {search ? 'Try a different search term.' : 'Staff members will appear here once added.'}
          </p>
        </div>
      ) : viewMode === 'table' ? (
        <TableView staff={filtered} theme={theme} onMessage={onOpenMessages} />
      ) : (
        <CardView staff={filtered} theme={theme} onMessage={onOpenMessages} />
      )}
    </div>
  );
}
